import { Trash2 } from "lucide-react";
import {
  JOURNEY_ACTION_TYPES,
  JOURNEY_ACTION_TYPE_LABELS,
  type JourneyAction,
  type JourneyActionType,
} from "@ckgo2/shared";

export interface BuilderOptions {
  users: Array<{ id: string; name: string }>;
  segments: Array<{ id: string; name: string }>;
  templates: Array<{ slug: string; name: string }>;
}

const sel =
  "rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm";
const inp = sel + " w-full";

// All v1 action types are offered. SMS/WhatsApp are a stub channel (sends record skipped:
// channel_not_configured until the provider lands in řez 3) — the editor flags that inline.
const BUILDER_ACTION_TYPES: JourneyActionType[] = [...JOURNEY_ACTION_TYPES];

/** A well-formed default action for a freshly-picked type. */
function defaultAction(type: JourneyActionType): JourneyAction {
  switch (type) {
    case "send_email":
      return { type, recipient: "customer" };
    case "send_sms":
      return { type, recipient: "customer" };
    case "send_whatsapp":
      return { type, recipient: "customer" };
    case "create_task":
      return { type, title: "" };
    case "assign":
      return { type, mode: "round_robin" };
    case "notify_user":
      return { type, target: "subject_assignee", message: "" };
    case "add_to_segment":
      return { type, segmentId: "" };
    case "remove_from_segment":
      return { type, segmentId: "" };
    case "generate_document":
      return { type, docType: "contract" };
    case "webhook":
      return { type, url: "" };
    case "agent_action":
      return { type, instruction: "", allowMoney: false };
  }
}

export default function ActionEditor({
  value,
  onChange,
  onRemove,
  options,
}: {
  value: JourneyAction;
  onChange: (a: JourneyAction) => void;
  onRemove: () => void;
  options: BuilderOptions;
}) {
  // Narrow per branch with a loose patcher (the union is validated server-side on save).
  const patch = (p: Record<string, unknown>) => onChange({ ...value, ...p } as JourneyAction);

  return (
    <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
      <div className="flex items-center justify-between">
        <select
          className={sel}
          value={value.type}
          onChange={(e) => onChange(defaultAction(e.target.value as JourneyActionType))}
        >
          {BUILDER_ACTION_TYPES.map((t) => (
            <option key={t} value={t}>
              {JOURNEY_ACTION_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-gray-400 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {value.type === "send_email" && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <select
              className={sel}
              value={value.recipient}
              onChange={(e) => patch({ recipient: e.target.value })}
            >
              <option value="customer">to customer</option>
              <option value="assignee">to assignee</option>
              <option value="admin">to admin</option>
              <option value="email_field">to a field…</option>
            </select>
            {value.recipient === "email_field" && (
              <input
                className={sel}
                placeholder="vars key (e.g. email)"
                value={value.emailField ?? ""}
                onChange={(e) => patch({ emailField: e.target.value })}
              />
            )}
            <select
              className={sel}
              value={value.templateSlug ?? ""}
              onChange={(e) => patch({ templateSlug: e.target.value || undefined })}
            >
              <option value="">— inline subject/body —</option>
              {options.templates.map((t) => (
                <option key={t.slug} value={t.slug}>
                  template: {t.name}
                </option>
              ))}
            </select>
          </div>
          {!value.templateSlug && (
            <>
              <input
                className={inp}
                placeholder="Subject (supports {{vars}})"
                value={value.subject ?? ""}
                onChange={(e) => patch({ subject: e.target.value })}
              />
              <textarea
                className={inp}
                rows={3}
                placeholder="HTML body (supports {{vars}})"
                value={value.body ?? ""}
                onChange={(e) => patch({ body: e.target.value })}
              />
            </>
          )}
        </div>
      )}

      {(value.type === "send_sms" || value.type === "send_whatsapp") && (
        <div className="space-y-2">
          <select
            className={sel}
            value={value.recipient}
            onChange={(e) => patch({ recipient: e.target.value })}
          >
            <option value="customer">to customer</option>
            <option value="assignee">to assignee</option>
            <option value="phone_field">to a field…</option>
          </select>
          <textarea
            className={inp}
            rows={2}
            placeholder="Message (supports {{vars}})"
            value={value.body ?? ""}
            onChange={(e) => patch({ body: e.target.value })}
          />
          <p className="text-xs text-amber-600">
            Channel not yet configured — sends are recorded as skipped.
          </p>
        </div>
      )}

      {value.type === "create_task" && (
        <div className="space-y-2">
          <input
            className={inp}
            placeholder="Task title (supports {{vars}})"
            value={value.title}
            onChange={(e) => patch({ title: e.target.value })}
          />
          <div className="flex flex-wrap gap-2">
            <input
              type="number"
              className={sel + " w-28"}
              placeholder="due in days"
              value={value.dueInDays ?? ""}
              onChange={(e) =>
                patch({ dueInDays: e.target.value === "" ? undefined : Number(e.target.value) })
              }
            />
            <select
              className={sel}
              value={value.kind ?? "manual"}
              onChange={(e) => patch({ kind: e.target.value })}
            >
              <option value="manual">task</option>
              <option value="callback">callback</option>
              <option value="supplier_followup">supplier follow-up</option>
            </select>
            <select
              className={sel}
              value={value.assignTo ?? ""}
              onChange={(e) => patch({ assignTo: e.target.value || undefined })}
            >
              <option value="">unassigned</option>
              <option value="subject_assignee">subject's owner</option>
              <option value="user">specific user…</option>
            </select>
            {value.assignTo === "user" && (
              <select
                className={sel}
                value={value.userId ?? ""}
                onChange={(e) => patch({ userId: e.target.value })}
              >
                <option value="">—</option>
                {options.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {value.type === "assign" && (
        <div className="flex flex-wrap gap-2">
          <select
            className={sel}
            value={value.mode}
            onChange={(e) => patch({ mode: e.target.value })}
          >
            <option value="round_robin">round robin</option>
            <option value="user">specific user</option>
          </select>
          {value.mode === "user" && (
            <select
              className={sel}
              value={value.userId ?? ""}
              onChange={(e) => patch({ userId: e.target.value })}
            >
              <option value="">—</option>
              {options.users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {value.type === "notify_user" && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <select
              className={sel}
              value={value.target}
              onChange={(e) => patch({ target: e.target.value })}
            >
              <option value="subject_assignee">subject's owner</option>
              <option value="user">specific user</option>
            </select>
            {value.target === "user" && (
              <select
                className={sel}
                value={value.userId ?? ""}
                onChange={(e) => patch({ userId: e.target.value })}
              >
                <option value="">—</option>
                {options.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <input
            className={inp}
            placeholder="Notification message (supports {{vars}})"
            value={value.message}
            onChange={(e) => patch({ message: e.target.value })}
          />
        </div>
      )}

      {(value.type === "add_to_segment" || value.type === "remove_from_segment") && (
        <select
          className={sel}
          value={value.segmentId}
          onChange={(e) => patch({ segmentId: e.target.value })}
        >
          <option value="">— pick segment —</option>
          {options.segments.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      )}

      {value.type === "generate_document" && (
        <select
          className={sel}
          value={value.docType}
          onChange={(e) => patch({ docType: e.target.value })}
        >
          <option value="contract">contract</option>
          <option value="itinerary">itinerary</option>
          <option value="voucher">voucher</option>
          <option value="package">travel package</option>
        </select>
      )}

      {value.type === "webhook" && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <select
              className={sel}
              value={value.method ?? "POST"}
              onChange={(e) => patch({ method: e.target.value })}
            >
              <option value="POST">POST</option>
              <option value="GET">GET</option>
            </select>
            <input
              className={inp}
              placeholder="https://…"
              value={value.url}
              onChange={(e) => patch({ url: e.target.value })}
            />
          </div>
          <textarea
            className={inp}
            rows={2}
            placeholder="Body template (supports {{vars}}); blank → default JSON"
            value={value.bodyTemplate ?? ""}
            onChange={(e) => patch({ bodyTemplate: e.target.value || undefined })}
          />
        </div>
      )}

      {value.type === "agent_action" && (
        <div className="space-y-2">
          <textarea
            className={inp}
            rows={3}
            placeholder="Instruction for the agent (supports {{vars}}), e.g. “This customer last travelled {{lastTripDate}}. Draft a personalised win-back offer.”"
            value={value.instruction}
            onChange={(e) => patch({ instruction: e.target.value })}
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={value.allowMoney ?? false}
              onChange={(e) => patch({ allowMoney: e.target.checked })}
            />
            Allow money actions (invoices, refunds, date changes…)
          </label>
          <p className="text-xs text-gray-500">
            The agent only <strong>proposes</strong> — a person approves it in the agent queue.
            Nothing is sent or charged automatically.
          </p>
        </div>
      )}
    </div>
  );
}
